import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, X, ZoomIn, ZoomOut } from 'lucide-react';

import { buildAssetPreviewUrl } from '../../utils/mediaUrl';
import { buildEmbeddedImageSrc, type EmbeddedImageData } from './embeddedImageUtils';

type TtqibZoomableImageOverlayProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  record?: EmbeddedImageData;
  fullSourceUrl?: string;
};

const MIN_SCALE = 1;
const MAX_SCALE = 6;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function touchDistance(touches: ArrayLike<{ clientX: number; clientY: number }>): number {
  if (touches.length < 2) return 0;
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
}

function resolveViewerSrc(record?: EmbeddedImageData, fullSourceUrl?: string): string | null {
  const base = (fullSourceUrl || record?.sourceImageUrl || '').trim();
  if (!base) return null;

  // 已是带 x-oss-process 的裁切签名链（含签名内 process）→ 直接用，避免二次拼参破坏签名
  if (base.includes('x-oss-process=')) {
    return buildAssetPreviewUrl(base) || base;
  }

  if (record) {
    return buildEmbeddedImageSrc({ ...record, sourceImageUrl: base }) || buildAssetPreviewUrl(base);
  }
  return buildAssetPreviewUrl(base);
}

export function TtqibZoomableImageOverlay({
  open,
  onClose,
  title = '图片',
  record,
  fullSourceUrl,
}: TtqibZoomableImageOverlayProps) {
  const src = useMemo(() => resolveViewerSrc(record, fullSourceUrl), [record, fullSourceUrl]);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const pinchRef = useRef<{ distance: number; scale: number } | null>(null);

  const resetView = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (open) resetView();
  }, [open, resetView, src]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const adjustScale = useCallback((delta: number) => {
    setScale((prev) => clamp(Number((prev + delta).toFixed(2)), MIN_SCALE, MAX_SCALE));
  }, []);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch' && event.buttons === 0) return;
    dragRef.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.style.cursor = 'grabbing';
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const dx = event.clientX - dragRef.current.x;
    const dy = event.clientY - dragRef.current.y;
    dragRef.current = { x: event.clientX, y: event.clientY };
    setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
  }, []);

  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    event.currentTarget.style.cursor = scale > 1 ? 'grab' : 'zoom-in';
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, [scale]);

  const handleTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2) {
      pinchRef.current = { distance: touchDistance(event.touches), scale };
    }
  }, [scale]);

  const handleTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 2 || !pinchRef.current) return;
    event.preventDefault();
    const distance = touchDistance(event.touches);
    if (distance <= 0) return;
    const ratio = distance / pinchRef.current.distance;
    setScale(clamp(Number((pinchRef.current.scale * ratio).toFixed(2)), MIN_SCALE, MAX_SCALE));
  }, []);

  const handleTouchEnd = useCallback(() => {
    pinchRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.15 : 0.15;
      setScale((prev) => clamp(Number((prev + delta).toFixed(2)), MIN_SCALE, MAX_SCALE));
    };

    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [open]);

  if (!open || !src) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/85"
      onClick={onClose}
    >
      <div
        className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-3 text-white"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{title}</p>
          <p className="text-[11px] text-white/60">滚轮或按钮缩放 · 拖拽平移</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10"
            aria-label="缩小"
            onClick={() => adjustScale(-0.25)}
          >
            <ZoomOut size={16} />
          </button>
          <span className="w-12 text-center text-xs tabular-nums">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10"
            aria-label="放大"
            onClick={() => adjustScale(0.25)}
          >
            <ZoomIn size={16} />
          </button>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10"
            aria-label="重置视图"
            onClick={resetView}
          >
            <RotateCcw size={16} />
          </button>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10"
            aria-label="关闭"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div
        ref={viewportRef}
        className="relative min-h-0 flex-1 touch-none select-none overflow-hidden"
        style={{ cursor: scale > 1 ? 'grab' : 'zoom-in' }}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDoubleClick={resetView}
      >
        <div className="flex h-full w-full items-center justify-center">
          <img
            src={src}
            alt={title}
            draggable={false}
            referrerPolicy="no-referrer"
            className="max-h-none max-w-none select-none"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transformOrigin: 'center center',
              maxHeight: scale === 1 ? 'min(85vh, 100%)' : undefined,
              maxWidth: scale === 1 ? 'min(92vw, 100%)' : undefined,
              objectFit: 'contain',
            }}
          />
        </div>
      </div>
    </div>
  );
}
